import { useCallback, useMemo } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  cursorNavigation,
  nextCursor,
  useDrainAll,
} from "@restomenu/core/client";
import { api, fetchClient } from "@/core/api-client";
import {
  GUEST_PRODUCTS_LIST_KEY,
  MENU_ORDER_TYPE,
  MENU_PAGE_SIZE,
} from "./constants";

/**
 * Гостевые запросы меню.
 *
 * Ключ кэша — pointId, никогда brandId. Меню резолвится по точке, и у
 * бренда с включённой интеграцией на разных точках меню РАЗНЫЕ: brandId в
 * ответе — эхо бренда точки, а не ключ, по которому словарь можно
 * кэшировать (схема, спека §4.4).
 *
 * Сужение выдачи задают константы контроллера, а не клиент: только
 * isEnabled и только привязанное к паре (pointId, orderType), у категорий —
 * ещё и только непустые. Ослабить их параметром нельзя.
 *
 * С круга 6 те же условия сужают и СОСТАВ группы вариантов: размер, не
 * продающийся на этой точке, внутрь productVariants[] не приезжает.
 * Поэтому isAvailable в ответе всегда true — поле осталось формой.
 */

export function useGuestCategories(pointId: string | null) {
  return api.useQuery(
    "get",
    "/guest/categories",
    {
      params: {
        query: { pointId: pointId ?? "", orderType: MENU_ORDER_TYPE },
      },
    },
    { enabled: Boolean(pointId) },
  );
}

/**
 * Меню точки целиком: обход страниц с автодочиткой.
 *
 * С круга 7 меню одной выдачей не приезжает — потолок страницы стоит
 * всегда. Виджет строит по списку и клиентский поиск, и группировку по
 * категориям, и набор id картинок: всему этому нужно полное меню, поэтому
 * страницы дочитываются до конца, а форма возврата остаётся массивом.
 *
 * Написан руками, а не через api.useInfiniteQuery: тот подмешивает курсор в
 * тот же init и не умеет убрать sort, а сервер не принимает sort рядом с
 * after. Здесь sort и не задаётся, но обход обязан пережить его появление,
 * не превратившись в 400 на второй странице.
 */
export function useGuestProducts(pointId: string | null) {
  const query = useInfiniteQuery({
    queryKey: [...GUEST_PRODUCTS_LIST_KEY, pointId],
    // Путь для reportError: у ручного обхода второй элемент ключа — "list",
    // и без meta запрос не опознался бы гостевым. Тогда 404 о точке (а это
    // самый частый дефект — битая ссылка с чужим pointId) уехал бы гостю
    // красным снекбаром вместо тихого экрана «точка не найдена».
    meta: { path: "/guest/products" },
    enabled: Boolean(pointId),
    initialPageParam: null as string | null,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam, signal }) => {
      // pointId непуст: с пустым запрос не запускается (enabled выше).
      const { data, error } = await fetchClient.GET("/guest/products", {
        signal,
        params: {
          query: {
            // pointId и orderType в курсор не кладутся и шлются заново на
            // каждой странице.
            pointId: pointId ?? "",
            orderType: MENU_ORDER_TYPE,
            ...cursorNavigation(pageParam, {}),
            limit: MENU_PAGE_SIZE,
          },
        },
      });
      if (error) throw error;
      // Деструктуризация ломает дискриминацию union'а FetchResponse: без
      // этой строки тип data остаётся `GuestProductPage | undefined`.
      if (!data) throw new Error("GET /guest/products: пустой ответ");
      return data;
    },
    getNextPageParam: nextCursor,
  });

  const onLimitReached = useCallback(() => {
    // console, а не reportError: тот показывает гостю красный снекбар с
    // текстом ошибки, и в него уехало бы имя маршрута.
    console.error(
      "GET /guest/products: обход упёрся в потолок страниц — меню показано не полностью",
    );
  }, []);
  const { isDraining, limitReached } = useDrainAll(query, { onLimitReached });

  const data = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? undefined,
    [query.data],
  );

  const loadedPages = query.data?.pages.length ?? 0;

  // Возвращаем ЧЕТЫРЕ поля, а не весь результат TanStack. Спред отдал бы
  // наружу status/isSuccess/isPending, рассогласованные с нашим isLoading
  // (после первой страницы status уже "success"), и fetchNextPage, ручной
  // вызов которого сорвал бы инвариант дочитки. Потребитель — useMenuData.
  return {
    data,
    // До конца дочитки, но НЕ по hasNextPage: блок ошибки в ProductList
    // стоит под {error && !loading}, и залипший спиннер спрятал бы и
    // ошибку, и кнопку повтора.
    isLoading: query.isLoading || isDraining,
    error: query.error,
    // Меню дочитано частично: обход оборвался ошибкой либо упёрся в потолок.
    // Гостю показываем то, что приехало, — «Ошибка загрузки» вместо половины
    // меню хуже, чем половина меню с предупреждением.
    isPartial: loadedPages > 0 && (Boolean(query.error) || limitReached),
  };
}

/**
 * Погасить кэш меню целиком — и словари по путям, и обход товаров.
 *
 * Обход живёт под собственным ключом, и `useInvalidate(["/guest/products"])`
 * его не накрывает: TanStack сравнивает ЭЛЕМЕНТЫ ключа, а у обхода первый
 * элемент — "guest-products", а не "get".
 */
export function useInvalidateGuestMenu(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["get", "/guest/categories"] });
    // Товары — только своим ключом. Строки `["get", "/guest/products"]`
    // здесь нет намеренно: под ней после круга 7 не живёт ни один запрос,
    // и она читалась бы как «товары гасятся и по пути тоже».
    void qc.invalidateQueries({ queryKey: GUEST_PRODUCTS_LIST_KEY });
  }, [qc]);
}

export function useGuestProductTags(pointId: string | null) {
  return api.useQuery(
    "get",
    "/guest/product-tags",
    { params: { query: { pointId: pointId ?? "" } } },
    { enabled: Boolean(pointId) },
  );
}

export function useGuestVariantProperties(pointId: string | null) {
  return api.useQuery(
    "get",
    "/guest/product-variant-properties",
    { params: { query: { pointId: pointId ?? "" } } },
    { enabled: Boolean(pointId) },
  );
}

/**
 * Словарь ГРУПП опций меню.
 *
 * orderType здесь НЕ передаётся, в отличие от соседей: маршрут его не
 * принимает — точечного измерения у группы в домене нет, группы приезжают
 * меню целиком. Расхождение намеренное, не «забытый параметр».
 */
export function useGuestOptionGroups(pointId: string | null) {
  return api.useQuery(
    "get",
    "/guest/option-groups",
    { params: { query: { pointId: pointId ?? "" } } },
    { enabled: Boolean(pointId) },
  );
}

/**
 * Словарь ОПЦИЙ, суженный точкой и типом заказа. Цена в ответе уже
 * разрешена доменом — считать клиенту нечего.
 */
export function useGuestOptions(pointId: string | null) {
  return api.useQuery(
    "get",
    "/guest/options",
    {
      params: { query: { pointId: pointId ?? "", orderType: MENU_ORDER_TYPE } },
    },
    { enabled: Boolean(pointId) },
  );
}
